import { useEffect, useRef, useState } from "react";
import {
  useEntitlementsQuery,
  useApiKeyStatusQuery,
  useSetApiKeyMutation,
  useDeleteApiKeyMutation,
  useGeminiKeyStatusQuery,
  useSetGeminiKeyMutation,
  useDeleteGeminiKeyMutation,
  useClaudeAccountStatusQuery,
  useSetClaudeAccountMutation,
  useDeleteClaudeAccountMutation,
  useOpenAiKeyStatusQuery,
  useSetOpenAiKeyMutation,
  useDeleteOpenAiKeyMutation,
  useOpenAiAccountStatusQuery,
  useSetOpenAiAccountMutation,
  useDeleteOpenAiAccountMutation,
  useOpencodeKeyStatusQuery,
  useSetOpencodeKeyMutation,
  useDeleteOpencodeKeyMutation,
} from "@/api/settings";

type ProviderId = "anthropic" | "openai" | "opencode" | "google";
type AuthMethod = "subscription" | "api_key";

/** One consolidated credentials section: pick the provider, then how to
 *  authenticate with it — API key or subscription login. Replaces the three
 *  separate API Key / Gemini / Claude Account cards. API-key methods sit
 *  behind the BYOK entitlement; subscription connect is always available
 *  (it's how internal deployments authenticate). */
export function ProviderCredentialsCard() {
  const { data: entitlements } = useEntitlementsQuery();
  // Default true while loading so entitled users don't flash the upsell;
  // the gate is enforced server-side regardless.
  const byokAllowed = entitlements?.byok ?? true;

  const { data: apiKeyStatus } = useApiKeyStatusQuery();
  const setApiKey = useSetApiKeyMutation();
  const deleteApiKey = useDeleteApiKeyMutation();
  const hasKey = apiKeyStatus?.has_key ?? false;

  const { data: geminiStatus } = useGeminiKeyStatusQuery();
  const setGeminiKey = useSetGeminiKeyMutation();
  const deleteGeminiKey = useDeleteGeminiKeyMutation();
  const hasGeminiKey = geminiStatus?.configured ?? false;

  const { data: claudeAccountStatus } = useClaudeAccountStatusQuery();
  const setClaudeAccount = useSetClaudeAccountMutation();
  const deleteClaudeAccount = useDeleteClaudeAccountMutation();
  const hasClaudeAccount = claudeAccountStatus?.configured ?? false;

  const { data: openaiKeyStatus } = useOpenAiKeyStatusQuery();
  const setOpenAiKey = useSetOpenAiKeyMutation();
  const deleteOpenAiKey = useDeleteOpenAiKeyMutation();
  const hasOpenAiKey = openaiKeyStatus?.configured ?? false;
  const { data: openaiAccountStatus } = useOpenAiAccountStatusQuery();
  const setOpenAiAccount = useSetOpenAiAccountMutation();
  const deleteOpenAiAccount = useDeleteOpenAiAccountMutation();
  const hasOpenAiAccount = openaiAccountStatus?.configured ?? false;

  const { data: opencodeStatus } = useOpencodeKeyStatusQuery();
  const setOpencodeKey = useSetOpencodeKeyMutation();
  const deleteOpencodeKey = useDeleteOpencodeKeyMutation();
  const hasOpencodeKey = opencodeStatus?.configured ?? false;
  const [opencodeKeyInput, setOpencodeKeyInput] = useState("");

  const [apiKeyInput, setApiKeyInput] = useState("");
  const [geminiKeyInput, setGeminiKeyInput] = useState("");
  const [claudeTokenInput, setClaudeTokenInput] = useState("");
  const [openaiKeyInput, setOpenaiKeyInput] = useState("");
  const [openaiAuthInput, setOpenaiAuthInput] = useState("");

  const [provider, setProvider] = useState<ProviderId>("anthropic");
  const [method, setMethod] = useState<AuthMethod>("subscription");

  // Seed the Anthropic method from what's actually configured, once the
  // statuses load — a key-only user lands on the API-key form, everyone
  // else on subscription (the internal-deployment default). One-shot so a
  // user's manual tab choice is never clobbered by a refetch.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (apiKeyStatus === undefined || claudeAccountStatus === undefined) return;
    seededRef.current = true;
    if (hasKey && !hasClaudeAccount) setMethod("api_key");
  }, [apiKeyStatus, claudeAccountStatus, hasKey, hasClaudeAccount]);

  const anthropicConfigured = hasKey || hasClaudeAccount;
  const openaiConfigured = hasOpenAiKey || hasOpenAiAccount;

  const providerTabs: { id: ProviderId; label: string; configured: boolean }[] = [
    { id: "anthropic", label: "Anthropic (Claude)", configured: anthropicConfigured },
    { id: "openai", label: "OpenAI (GPT)", configured: openaiConfigured },
    { id: "opencode", label: "OpenCode", configured: hasOpencodeKey },
    { id: "google", label: "Google (Gemini)", configured: hasGeminiKey },
  ];

  const byokUpsell = (
    <p className="rounded-lg border border-line bg-surface px-3 py-2.5 text-xs text-ink-3">
      Bring-your-own API keys are available on the Enterprise plan. Your
      generations are billed through the platform — or connect a Claude
      subscription instead.
    </p>
  );

  return (
    <div className="mt-6 rounded-xl border border-line bg-surface-2">
      <div className="border-b border-line px-6 py-4">
        <h2 className="text-sm font-semibold text-ink">Model Providers</h2>
      </div>
      <div className="space-y-4 p-6">
        <p className="text-xs text-ink-3">
          Connect the AI providers that power your generations. Each provider
          takes an API key or a subscription login; your credentials are
          encrypted at rest and only ever used for your own sessions.
        </p>

        {/* Provider selector */}
        <div className="flex gap-2">
          {providerTabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setProvider(t.id)}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                provider === t.id
                  ? "border-accent bg-accent-soft text-ink"
                  : "border-line bg-surface text-ink-2 hover:bg-surface-3"
              }`}
            >
              <span
                className={`inline-block h-2 w-2 rounded-full ${
                  t.configured ? "bg-success" : "bg-ink-3"
                }`}
              />
              {t.label}
            </button>
          ))}
        </div>

        {provider === "anthropic" && (
          <div className="space-y-4">
            {/* Auth method toggle */}
            <div className="flex gap-1 rounded-lg border border-line bg-surface p-1 text-xs font-medium">
              {(
                [
                  ["subscription", "Claude subscription"],
                  ["api_key", "API key"],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setMethod(id)}
                  className={`flex-1 rounded-md px-3 py-1.5 transition-colors ${
                    method === id
                      ? "bg-accent text-accent-fg"
                      : "text-ink-2 hover:text-ink"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {hasKey && hasClaudeAccount && (
              <p className="text-xs text-ink-3">
                Both are connected — the API key takes precedence for billing.
              </p>
            )}

            {method === "subscription" ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-block h-2 w-2 rounded-full ${hasClaudeAccount ? "bg-success" : "bg-ink-3"}`}
                  />
                  <span className="text-sm text-ink-2">
                    {hasClaudeAccount
                      ? "Claude account connected"
                      : "No Claude account connected"}
                  </span>
                </div>
                <input
                  type="password"
                  value={claudeTokenInput}
                  onChange={(e) => setClaudeTokenInput(e.target.value)}
                  placeholder="Paste the token from `claude setup-token`"
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
                />
                <p className="text-xs text-ink-3">
                  Run <code className="rounded bg-code px-1">claude setup-token</code>{" "}
                  in your terminal and paste the result. Your generations then
                  run on your own Claude subscription.
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      if (!claudeTokenInput.trim()) return;
                      setClaudeAccount.mutate(claudeTokenInput.trim(), {
                        onSuccess: () => setClaudeTokenInput(""),
                      });
                    }}
                    disabled={!claudeTokenInput.trim() || setClaudeAccount.isPending}
                    className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {setClaudeAccount.isPending ? "Connecting..." : "Connect"}
                  </button>
                  {hasClaudeAccount && (
                    <button
                      onClick={() => deleteClaudeAccount.mutate()}
                      disabled={deleteClaudeAccount.isPending}
                      className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-danger transition-colors hover:bg-danger-soft disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {deleteClaudeAccount.isPending ? "Disconnecting..." : "Disconnect"}
                    </button>
                  )}
                </div>
                {setClaudeAccount.isError && (
                  <p className="text-xs text-danger">Failed to save the token.</p>
                )}
                {setClaudeAccount.isSuccess && (
                  <p className="text-xs text-success">Claude account connected.</p>
                )}
              </div>
            ) : !byokAllowed ? (
              byokUpsell
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-block h-2 w-2 rounded-full ${hasKey ? "bg-success" : "bg-ink-3"}`}
                  />
                  <span className="text-sm text-ink-2">
                    {hasKey ? "Anthropic API key is set" : "No API key configured"}
                  </span>
                </div>
                <input
                  type="password"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder="sk-ant-..."
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
                />
                <p className="text-xs text-ink-3">
                  Usage-billed to your own Anthropic account. Encrypted at rest.
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      if (!apiKeyInput.trim()) return;
                      setApiKey.mutate(apiKeyInput.trim(), {
                        onSuccess: () => setApiKeyInput(""),
                      });
                    }}
                    disabled={!apiKeyInput.trim() || setApiKey.isPending}
                    className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {setApiKey.isPending ? "Saving..." : "Save Key"}
                  </button>
                  {hasKey && (
                    <button
                      onClick={() => deleteApiKey.mutate()}
                      disabled={deleteApiKey.isPending}
                      className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-danger transition-colors hover:bg-danger-soft disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {deleteApiKey.isPending ? "Removing..." : "Remove Key"}
                    </button>
                  )}
                </div>
                {setApiKey.isError && (
                  <p className="text-xs text-danger">Failed to save API key. Please try again.</p>
                )}
                {setApiKey.isSuccess && (
                  <p className="text-xs text-success">API key saved successfully.</p>
                )}
                {deleteApiKey.isSuccess && (
                  <p className="text-xs text-success">API key removed.</p>
                )}
              </div>
            )}
          </div>
        )}

        {provider === "openai" && (
          <div className="space-y-4">
            <p className="rounded-lg border border-line bg-surface px-3 py-2.5 text-xs text-ink-3">
              Credentials save now; OpenAI-powered generation is coming — your
              connection will light up the moment the engine ships.
            </p>
            <div className="flex gap-1 rounded-lg border border-line bg-surface p-1 text-xs font-medium">
              {(
                [
                  ["subscription", "ChatGPT subscription"],
                  ["api_key", "API key"],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setMethod(id)}
                  className={`flex-1 rounded-md px-3 py-1.5 transition-colors ${
                    method === id
                      ? "bg-accent text-accent-fg"
                      : "text-ink-2 hover:text-ink"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {method === "subscription" ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-block h-2 w-2 rounded-full ${hasOpenAiAccount ? "bg-success" : "bg-ink-3"}`}
                  />
                  <span className="text-sm text-ink-2">
                    {hasOpenAiAccount
                      ? "ChatGPT account connected"
                      : "No ChatGPT account connected"}
                  </span>
                </div>
                <textarea
                  value={openaiAuthInput}
                  onChange={(e) => setOpenaiAuthInput(e.target.value)}
                  rows={3}
                  placeholder='Paste the contents of ~/.codex/auth.json'
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 font-mono text-xs text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
                />
                <p className="text-xs text-ink-3">
                  Run <code className="rounded bg-code px-1">codex login</code> in
                  your terminal, then paste the contents of{" "}
                  <code className="rounded bg-code px-1">~/.codex/auth.json</code>.
                  Stored encrypted.
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      if (!openaiAuthInput.trim()) return;
                      setOpenAiAccount.mutate(openaiAuthInput.trim(), {
                        onSuccess: () => setOpenaiAuthInput(""),
                      });
                    }}
                    disabled={!openaiAuthInput.trim() || setOpenAiAccount.isPending}
                    className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {setOpenAiAccount.isPending ? "Connecting..." : "Connect"}
                  </button>
                  {hasOpenAiAccount && (
                    <button
                      onClick={() => deleteOpenAiAccount.mutate()}
                      disabled={deleteOpenAiAccount.isPending}
                      className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-danger transition-colors hover:bg-danger-soft disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {deleteOpenAiAccount.isPending ? "Disconnecting..." : "Disconnect"}
                    </button>
                  )}
                </div>
                {setOpenAiAccount.isError && (
                  <p className="text-xs text-danger">
                    That didn't look like auth.json — paste the file's full JSON contents.
                  </p>
                )}
                {setOpenAiAccount.isSuccess && (
                  <p className="text-xs text-success">ChatGPT account connected.</p>
                )}
              </div>
            ) : !byokAllowed ? (
              byokUpsell
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-block h-2 w-2 rounded-full ${hasOpenAiKey ? "bg-success" : "bg-ink-3"}`}
                  />
                  <span className="text-sm text-ink-2">
                    {hasOpenAiKey ? "OpenAI API key is set" : "No API key configured"}
                  </span>
                </div>
                <input
                  type="password"
                  value={openaiKeyInput}
                  onChange={(e) => setOpenaiKeyInput(e.target.value)}
                  placeholder="sk-..."
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
                />
                <p className="text-xs text-ink-3">
                  Usage-billed to your own OpenAI account. Encrypted at rest.
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      if (!openaiKeyInput.trim()) return;
                      setOpenAiKey.mutate(openaiKeyInput.trim(), {
                        onSuccess: () => setOpenaiKeyInput(""),
                      });
                    }}
                    disabled={!openaiKeyInput.trim() || setOpenAiKey.isPending}
                    className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {setOpenAiKey.isPending ? "Saving..." : "Save Key"}
                  </button>
                  {hasOpenAiKey && (
                    <button
                      onClick={() => deleteOpenAiKey.mutate()}
                      disabled={deleteOpenAiKey.isPending}
                      className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-danger transition-colors hover:bg-danger-soft disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {deleteOpenAiKey.isPending ? "Removing..." : "Remove Key"}
                    </button>
                  )}
                </div>
                {setOpenAiKey.isError && (
                  <p className="text-xs text-danger">Failed to save OpenAI key.</p>
                )}
                {setOpenAiKey.isSuccess && (
                  <p className="text-xs text-success">OpenAI key saved.</p>
                )}
              </div>
            )}
          </div>
        )}

        {provider === "opencode" && (
          <div className="space-y-3">
            <p className="rounded-lg border border-line bg-surface px-3 py-2.5 text-xs text-ink-3">
              One OpenCode API key (from opencode.ai/auth) covers both their
              plans — Go (the $10/mo subscription) and Zen (pay-as-you-go
              balance). Third-party use is officially supported. Key saves
              now; OpenCode-powered generation is coming.
            </p>
            <div className="flex items-center gap-2">
              <span
                className={`inline-block h-2 w-2 rounded-full ${hasOpencodeKey ? "bg-success" : "bg-ink-3"}`}
              />
              <span className="text-sm text-ink-2">
                {hasOpencodeKey ? "OpenCode key is set" : "No OpenCode key configured"}
              </span>
            </div>
            <input
              type="password"
              value={opencodeKeyInput}
              onChange={(e) => setOpencodeKeyInput(e.target.value)}
              placeholder="Paste your OPENCODE_API_KEY"
              className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
            />
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  if (!opencodeKeyInput.trim()) return;
                  setOpencodeKey.mutate(opencodeKeyInput.trim(), {
                    onSuccess: () => setOpencodeKeyInput(""),
                  });
                }}
                disabled={!opencodeKeyInput.trim() || setOpencodeKey.isPending}
                className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-40"
              >
                {setOpencodeKey.isPending ? "Saving..." : "Save Key"}
              </button>
              {hasOpencodeKey && (
                <button
                  onClick={() => deleteOpencodeKey.mutate()}
                  disabled={deleteOpencodeKey.isPending}
                  className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-danger transition-colors hover:bg-danger-soft disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {deleteOpencodeKey.isPending ? "Removing..." : "Remove Key"}
                </button>
              )}
            </div>
            {setOpencodeKey.isError && (
              <p className="text-xs text-danger">Failed to save OpenCode key.</p>
            )}
            {setOpencodeKey.isSuccess && (
              <p className="text-xs text-success">OpenCode key saved.</p>
            )}
          </div>
        )}

        {provider === "google" && (
          <div className="space-y-3">
            <p className="text-xs text-ink-3">
              Powers the image-restyle step of Image to vector (pixel-art
              redraws, style changes). API key only — Google's terms prohibit
              subscription (Login with Google) auth in third-party apps, and
              they enforce it with account suspensions.
            </p>
            {!byokAllowed ? (
              byokUpsell
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-block h-2 w-2 rounded-full ${hasGeminiKey ? "bg-success" : "bg-ink-3"}`}
                  />
                  <span className="text-sm text-ink-2">
                    {hasGeminiKey ? "Gemini API key is set" : "No Gemini key configured"}
                  </span>
                </div>
                <input
                  type="password"
                  value={geminiKeyInput}
                  onChange={(e) => setGeminiKeyInput(e.target.value)}
                  placeholder="AIza..."
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
                />
                <p className="text-xs text-ink-3">
                  Falls back to the server's key when unset. Encrypted at rest.
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      if (!geminiKeyInput.trim()) return;
                      setGeminiKey.mutate(geminiKeyInput.trim(), {
                        onSuccess: () => setGeminiKeyInput(""),
                      });
                    }}
                    disabled={!geminiKeyInput.trim() || setGeminiKey.isPending}
                    className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {setGeminiKey.isPending ? "Saving..." : "Save Key"}
                  </button>
                  {hasGeminiKey && (
                    <button
                      onClick={() => deleteGeminiKey.mutate()}
                      disabled={deleteGeminiKey.isPending}
                      className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-danger transition-colors hover:bg-danger-soft disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {deleteGeminiKey.isPending ? "Removing..." : "Remove Key"}
                    </button>
                  )}
                </div>
                {setGeminiKey.isError && (
                  <p className="text-xs text-danger">Failed to save Gemini key.</p>
                )}
                {setGeminiKey.isSuccess && (
                  <p className="text-xs text-success">Gemini key saved.</p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
